import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot, UrlTree, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { filter, map, switchMap, take, timeout, catchError } from 'rxjs/operators';
import { NativeStorageService } from '../data/native-storage.service';
import { UserService } from './user-service';
import { from, of } from 'rxjs';
import { RedirectService } from './redirect.service';
import { LoginSelector, SettingsLoadedSelector } from '../settings/service/selectors';

@Injectable({
    providedIn: 'root'
})
export class UserGuard {
    private requiredRoles: string[] = [];

    constructor(
        private router: Router,
        private userService: UserService,
        private nativeStorageService: NativeStorageService,
        private redirectService: RedirectService,
        private store: Store
    ) {}

    /**
     * Waits for the login state to *settle* rather than sampling it after a fixed delay.
     *
     * This previously did `debounceTime(100), take(1)` on `userService.login$`, which is a
     * race on a hard page load: `SettingsService.login$` deliberately reports no user until
     * the session has been revalidated (`checked`), so on a deep-linked refresh the guard
     * sampled a logged-out state and bounced the user to `/`.
     *
     * Instead it waits for the persisted settings to be restored, then reads the raw stored
     * login: no stored user means genuinely logged out (decided immediately, no waiting),
     * and a stored user means waiting for revalidation to finish.
     */
    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
        console.log('UserGuard: canActivate called for', state.url);
        return from(this.nativeStorageService.checkServiceInit()).pipe(
            switchMap(() => {
                console.log('UserGuard: storage init complete, waiting for settings to load');
                return this.store.select(SettingsLoadedSelector);
            }),
            filter((loaded) => !!loaded),
            take(1),
            switchMap(() => this.store.select(LoginSelector)),
            filter((l) => !l?.user || !!l?.checked),
            take(1),
            timeout(10000),
            catchError((error) => {
                console.error('UserGuard: Error or timeout waiting for login state', error);
                // On error/timeout, assume not logged in and redirect to home
                this.redirectService.setRedirectUrl(state.url);
                this.router.navigateByUrl('/');
                return of({ checked: true, user: null, error: null, loading: false });
            }),
            map((l) => {
                console.log('UserGuard: evaluating access for', state.url, {
                    checked: l.checked,
                    user: l.user?.email,
                    roles: l.user?.roles
                });

                // If no user, deny access
                if (!l.user) {
                    console.log('UserGuard: Navigating back to home because user is not logged in');
                    // Store the intended URL for redirect after login
                    this.redirectService.setRedirectUrl(state.url);
                    this.router.navigateByUrl('/');
                    return false;
                }

                if (!userHasAccess(l.user)) {
                    console.log("UserGuard: Navigating back to home because user doesn't have access", l.user);
                    // Store the intended URL for redirect after login
                    this.redirectService.setRedirectUrl(state.url);
                    this.router.navigateByUrl('/');
                    return false;
                }

                const requiredRoles = route.data.requiredRoles;
                if (requiredRoles) {
                    // Check if user has any of the required roles
                    const hasRequiredRole = requiredRoles.some((role) => l.user.roles.includes(role));
                    if (!hasRequiredRole) {
                        // User does not have any of the required roles, redirect to unauthorized page
                        console.log("UserGuard: Navigating back to home because user doesn't have required roles", l.user);
                        // Store the intended URL for redirect after login
                        this.redirectService.setRedirectUrl(state.url);
                        this.router.navigateByUrl('/');
                        return false;
                    }
                }

                console.log('UserGuard: Access granted for', state.url);
                return true;
            })
        );
    }
}

export function userHasAccess(u) {
    return u?.roles?.includes('app_admin') || u?.roles?.includes('app_user');
}
